import React from 'react';

interface UnlimitedInkyMageImgProps {
    src: string;
    alt: string;
    className?: string;
    style?: React.CSSProperties;
    preloadImage?: boolean;
    width?: number;
    height?: number;
    sizes: {
        width: number;
        aspectRatio?: number;
    }[];
    onLoad?(img: HTMLImageElement): unknown
}

// given sizes = [300, 600, 900]
// 300 => can cover 0-300 without loss of quality
// 600 => can cover 301-600 without loss of quality
// 900 => can cover 601-... with a possible loss of quality

export default function UnlimitedInkyMageImg({
    src,
    alt,
    className,
    style,
    preloadImage,
    width,
    height,
    sizes,
    onLoad,
}: UnlimitedInkyMageImgProps): React.ReactElement {
    const sources = [];
    for (const format of ['webp', 'jpeg']) {
        for (let i = 0, l = sizes.length; i < l; i++) {
            const { width, aspectRatio } = sizes[i];

            let srcSet = src + `?f=${format}` + `&w=${width}`;
            if (aspectRatio !== undefined) {
                srcSet += `&h=${Math.ceil(width / 100) * 100 * aspectRatio}`;
            }
            srcSet = `${srcSet}, ${srcSet} 2x`;

            let media = '';
            if (i === 0) {
                media = `(max-width: ${width}px)`;
            } else {
                const { width: prevWidth } = sizes[i - 1];
                if (i === l - 1) {
                    media = `(min-width: ${prevWidth + 1}px)`;
                } else {
                    media = `(min-width: ${prevWidth + 1}px) and (max-width: ${width}px)`;
                }
            }
            sources.push(
                <source
                    key={`${format}-${width}`}
                    type={`image/${format}`}
                    srcSet={srcSet}
                    media={media}
                />
            );
        }
    }

    let fetchPriority: 'auto' | 'low' | 'high';
    if (preloadImage === false) {
        fetchPriority = 'low';
    } else if (preloadImage === true) {
        fetchPriority = 'high';
    } else {
        fetchPriority = 'auto';
    }

    let loading: 'lazy' | 'eager' | undefined;
    if (preloadImage === false) {
        loading = 'lazy';
    } else if (preloadImage === true) {
        loading = 'eager';
    } else {
        loading = undefined;
    }

    return (
        <picture>
            {sources}
            <img
                fetchpriority={fetchPriority}
                loading={loading}
                className={className}
                style={style}
                src={src}
                alt={alt}
                width={width}
                height={height}
                onLoad={(e) => {
                    if (!onLoad) {
                        return;
                    }
                    onLoad(e.currentTarget);
                }}
            />
        </picture>
    );
}
